/**
 * `SettingsGeneral` — ported from `Foyer_Dashboard_CURRENT.jsx` line 7763.
 *
 * Phase 1 cuts: the "Plan" row uses static Discovery copy (no metered
 * pricing line). The Time zone select is rendered (faithful port per
 * CLAUDE.md §8) but its value is NOT persisted — see ADR-0144 §2.3.
 *
 * Save wiring (ADR-0144):
 *   - Controlled `<input value={draft_name}>` with dirty tracking.
 *   - Save calls `api.sites.update(site_id, { name: trimmed })` and
 *     replaces the cached Site on success (`useSites().updateSite`).
 *   - 403 / 404 / generic errors surface as typed toasts.
 *   - Owner/admin only — viewer/reviewer get a read-only input and
 *     no Save button (the server is the source of truth — it 403s
 *     these roles regardless).
 *   - Time zone changes never enable Save and never call the API
 *     (verified-facts: the surface must not appear to save something
 *     it does not).
 */

import { useState } from 'react';
import { Check } from 'lucide-react';
import type { ReactNode } from 'react';

import type { Site, UserRole } from '@sill/types';

import { ApiError } from '../../api/http-client.js';
import { useApi } from '../../api/api-context.js';
import { useOptionalAuth } from '../../app/auth-context.js';
import { useSites, useToast } from '../../app/providers.js';
import { useC } from '../../lib/theme.js';
import { Card, Pill } from '../../components/atoms/index.js';
import { TimeZoneSelect } from '../../components/TimeZoneSelect.js';
import { useTimeZoneContext } from '../../app/time-zone-context.js';
import { BuyerDetailRetentionSection } from './BuyerDetailRetentionSection.js';
import { ReturningVisitorsSection } from './ReturningVisitorsSection.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

const SITE_NAME_MAX = 80;

export interface SettingsGeneralProps {
  site: Site;
  /**
   * Optional role override for tests that don't wire `<AuthProvider />`.
   * Production resolves via `useOptionalAuth()`.
   */
  role_override?: UserRole;
}

export function SettingsGeneral({
  site,
  role_override,
}: SettingsGeneralProps): JSX.Element {
  const C = useC();
  const api = useApi();
  const auth = useOptionalAuth();
  const { updateSite } = useSites();
  const { accountTimeZone } = useTimeZoneContext();
  const toast = useToast();

  const role: UserRole | null =
    role_override !== undefined
      ? role_override
      : auth !== null && auth.state.kind === 'authenticated'
        ? auth.state.user.role
        : null;
  // The dashboard hide-for-non-owner/admin is UX only — the server
  // enforces the same gate on PATCH (ADR-0144 §2.5).
  const can_write = role === 'owner' || role === 'admin';

  const [draft_name, setDraftName] = useState<string>(site.name);
  // ADR-0160 — per-site display-zone override; null = inherit account default.
  const [draft_tz, setDraftTz] = useState<string | null>(
    site.display_time_zone ?? null,
  );
  const [pending, setPending] = useState<boolean>(false);

  const trimmed = draft_name.trim();
  const name_valid = trimmed.length >= 1 && trimmed.length <= SITE_NAME_MAX;
  const name_dirty = trimmed !== site.name;
  const tz_dirty = draft_tz !== (site.display_time_zone ?? null);
  // Save enables when SOMETHING changed and the name (if it changed) is valid.
  const is_dirty = (name_dirty || tz_dirty) && (!name_dirty || name_valid);
  const save_disabled = !is_dirty || pending || !can_write;

  async function onSave(): Promise<void> {
    if (save_disabled) return;
    setPending(true);
    try {
      const patch: Partial<Site> = {};
      if (name_dirty) patch.name = trimmed;
      // '' clears the override → inherit the account default (the server
      // treats an empty zone as a clear, ADR-0160).
      if (tz_dirty) patch.display_time_zone = draft_tz ?? '';
      const updated = await api.sites.update(site.site_id, patch);
      // Replace the cached Site in the SiteProvider so the TopBar / sites list
      // / any consumer — and the effective display zone — see the change
      // immediately. The returned row is the server's canonical view; we do
      // NOT optimistically update before the response (ADR-0144 §5).
      updateSite(updated.site_id, updated);
      setDraftName(updated.name);
      setDraftTz(updated.display_time_zone ?? null);
      toast({ tone: 'lime', title: 'Settings saved.' });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.kind === 'forbidden') {
          toast({ tone: 'coral', title: 'Permission needed.' });
        } else if (err.kind === 'not_found') {
          toast({ tone: 'coral', title: 'Site not found — refresh the page.' });
        } else {
          toast({ tone: 'coral', title: "Couldn't save — try again." });
        }
      } else {
        toast({ tone: 'coral', title: "Couldn't save — try again." });
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <>
    <Card className="p-5 sm:p-6 mb-5">
      <div className="mb-5 pb-4" style={{ borderBottom: `1px solid ${C.border}` }}>
        <div
          style={{
            fontFamily: serifFont,
            fontSize: 22,
            color: C.text,
            letterSpacing: -0.5,
            marginBottom: 4,
          }}
        >
          General
        </div>
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
          Basics about this site.
        </div>
      </div>

      <Row C={C} label="Site name">
        <input
          aria-label="Site name"
          value={draft_name}
          maxLength={SITE_NAME_MAX}
          readOnly={!can_write}
          disabled={pending}
          onChange={(e) => setDraftName(e.target.value)}
          className="w-full px-3 py-2 outline-none"
          style={{
            fontFamily: sansFont,
            fontSize: 14,
            color: C.text,
            background: C.surface2,
            border: `1px solid ${C.border}`,
            borderRadius: 2,
          }}
        />
      </Row>
      <Row C={C} label="Domain" hint="Owned and verified. Change requires re-verification.">
        <div className="flex items-center gap-2">
          <span style={{ fontFamily: monoFont, fontSize: 13, color: C.text, flex: 1 }}>
            {site.domain}
          </span>
          <Pill tone="lime" icon={Check}>
            VERIFIED
          </Pill>
        </div>
      </Row>
      {/* The static "Plan" row was removed 2026-10-01 — the live plan
          now shows on Account → Billing, so the row would duplicate
          account-level state here. See the Account view's billing tab
          for current plan + usage. */}
      <Row
        C={C}
        label="Time zone"
        hint="How dates on this site's dashboard are shown. Overrides the account default; audit exports stay UTC."
      >
        {/* ADR-0160 — real per-site display-zone override. Empty = inherit the
         *  account default. `can_write` (owner/admin) gates the control; the
         *  server enforces the same on PATCH. */}
        <TimeZoneSelect
          aria-label="Time zone (display preference)"
          value={draft_tz}
          onChange={setDraftTz}
          disabled={!can_write || pending}
          inheritLabel={`Inherit account default (${accountTimeZone})`}
        />
      </Row>
      {can_write ? (
        <div className="pt-5 flex justify-end">
          <button
            type="button"
            onClick={() => void onSave()}
            disabled={save_disabled}
            className="px-5 py-2"
            style={{
              background: C.lime,
              color: C.ink,
              fontFamily: sansFont,
              fontWeight: 600,
              fontSize: 13,
              borderRadius: 2,
              opacity: save_disabled ? 0.5 : 1,
              cursor: save_disabled ? 'not-allowed' : 'pointer',
            }}
          >
            {pending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      ) : null}
    </Card>
    {/* ADR-0065 §8 — per-account buyer-detail retention window. Owner
     *  + admin only; the section renders nothing for reviewer/viewer. */}
    <BuyerDetailRetentionSection />
    {/* ADR-0243 — per-site returning-visitor tracking switch (owner/admin). */}
    <ReturningVisitorsSection siteId={site.site_id} />
    </>
  );
}

function Row({
  C,
  label,
  hint,
  children,
}: {
  C: { text: string; muted: string; borderSoft: string };
  label: string;
  hint?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6 py-4"
      style={{ borderBottom: `1px solid ${C.borderSoft}` }}
    >
      <div className="sm:col-span-1">
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 500 }}>
          {label}
        </div>
        {hint ? (
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 12,
              color: C.muted,
              marginTop: 2,
              lineHeight: 1.5,
            }}
          >
            {hint}
          </div>
        ) : null}
      </div>
      <div className="sm:col-span-2">{children}</div>
    </div>
  );
}
